/**
 * Where does tapping a bell notification go?
 *
 * Pulled out of NotificationsPage.jsx (2026-09-21, link audit) so it can be
 * tested against every notification shape the backend writes. Behaviour is
 * the page's original routing, with one addition: a `membership_approved`
 * row that only carries `communityId` (rows written before patch 0138)
 * opens the community instead of Home.
 *
 * Shapes handled (see the backend inserts in routes/*.py and scheduler/*):
 *   data.requestId | referenceId | requestId   -- the prayer a row is about
 *   data.confirmationId | confirmationId       -- a specific note/prayer on it
 *   data.communityId                           -- community rows
 *   data.url                                   -- an explicit same-origin path
 */
export function resolveNotificationTarget(notif) {
  const n = notif || {};
  const data = n.data || {};
  const requestId = data.requestId || n.referenceId || n.requestId;
  const confirmationId = data.confirmationId || n.confirmationId;
  const communityId = data.communityId;

  // Admin moderation flag — always honor the deep link to the moderation tab
  // ahead of the generic requestId routing so admins don't get bounced into
  // the requestor view of the flagged item.
  if (n.type === 'flag') {
    return data.url || (communityId ? `/communities/${communityId}/admin?tab=moderation` : '/');
  }
  // Nudge / system notifications that point to non-request URLs.
  if (n.type === 'create_community_nudge') return '/communities?explainer=1';
  if (n.type === 'resource_refresh') return data.url || '/resources/scriptures';
  if ((n.type === 'community_invite' || n.type === 'membership_approved') && communityId) {
    return `/communities/${communityId}`;
  }
  if (n.type === 'community_welcome') {
    // Custom-shaped notification from invite auto-enroll. Routes back to
    // the community detail page when we know which one to open.
    return communityId ? `/communities/${communityId}` : '/communities';
  }
  // Generic data.url so server-side notification types can ship without a
  // frontend change.
  if (!requestId && data.url) return data.url;

  if (requestId) {
    if (n.type === 'lifecycle_digest') {
      // Batched prompt covering several requests — open the queue focused on
      // the first rather than the single-request note composer.
      return `/pray-today?focus=${requestId}`;
    }
    if (n.type === 'day7_prompt' || n.type === 'day14_prompt' || n.type === 'day19_partner_visibility') {
      const mode = (n.type === 'day7_prompt' || n.type === 'day14_prompt') ? '&mode=checkin' : '';
      return `/pray-today?focus=${requestId}&compose=note${mode}`;
    }
    if (n.type === 'prayer_answered' || n.type === 'testimony_celebration') {
      // Land on the testimony block (top of the answered section), not the
      // confirmations list near the bottom.
      return `/requests/${requestId}#testimony`;
    }
    const hash = confirmationId ? `#confirmation-${confirmationId}` : '#confirmations-section';
    return `/requests/${requestId}${hash}`;
  }

  // Last resort: nothing tells us where to go, so route by type rather than
  // leaving the user stranded.
  if (n.type === 'on_behalf_claimed') return '/';
  if (n.type === 'community_invite' || n.type === 'community_welcome') return '/communities';
  if (n.type === 'resource_refresh') return '/resources/scriptures';
  return '/';
}

/** Coarse target type for click logging (never the raw url). */
export function classifyNotificationType(n) {
  const type = (n || {}).type;
  if (type === 'flag') return 'moderation';
  if (type === 'sms_launch') return 'sms_launch';
  if (type === 'verse_note' || type === 'encouragement') return 'note';
  if (type === 'prayer_answered' || type === 'testimony_celebration') return 'testimony';
  if (type === 'community_invite' || type === 'community_welcome') return 'invite';
  return type || 'other';
}

/**
 * The page a notification opens, in one word, for counting opens (P0215): 'pray' (the Pray page), 'share' (sharing a
 * request), 'home', 'request' (one request), 'group', 'profile' or 'other'. Never the address itself.
 */
export function openedPage(url) {
  let path = '';
  try {
    path = new URL(String(url || '/'), 'https://gr8fulprayers.com').pathname;
  } catch (_) {
    return 'other';
  }
  if (path === '/pray-today' || path.startsWith('/pray-today/')) return 'pray';
  if (path === '/requestor/submit') return 'share';
  if (path === '/requestor' || path.startsWith('/requestor/')) return 'home';
  if (path.startsWith('/requests/')) return 'request';
  if (path === '/communities' || path.startsWith('/communities/')) return 'group';
  if (path === '/profile' || path.startsWith('/profile/')) return 'profile';
  return 'other';
}

/** What a tap on a website push adds to the address it opens (public/sw.js), and src/index.js takes off again. */
export const PUSH_OPEN_PARAM = 'opened';

/**
 * { opened, href }: whether `href` carries the web push mark (`opened=push`), and the same address without it (path,
 * other parameters and fragment kept). An address without the mark comes back as it was, path and all.
 */
export function takePushOpenMark(href) {
  const url = new URL(String(href), 'https://gr8fulprayers.com');
  const opened = url.searchParams.get(PUSH_OPEN_PARAM) === 'push';
  if (opened) url.searchParams.delete(PUSH_OPEN_PARAM);
  return { opened, href: url.pathname + url.search + url.hash };
}
